import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Controls the GVS mobile navigation drawer and its nested sliding panels.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The root drawer details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #desktopMediaQuery = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    super.connectedCallback();

    /* Never allow a browser-restored <details open> state to survive page load. */
    this.#resetDrawer(false);

    /* MENU2B.2: when the richer Choosing Guide card points to the same
       destination as a normal final-row link, keep the card and remove the
       duplicate row from the mobile navigation journey. */
    this.#suppressDuplicateGuideRows();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavigationClick, true);
    this.#desktopMediaQuery.addEventListener('change', this.#onViewportChange);
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavigationClick, true);
    this.#desktopMediaQuery.removeEventListener('change', this.#onViewportChange);
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Hide a normal third-level link when the panel's richer Choosing Guide card
   * points to the exact same destination. Desktop navigation is unaffected.
   */
  #suppressDuplicateGuideRows() {
    this.querySelectorAll('.gvs-mobile-menu__panel--third').forEach((panel) => {
      const guide = panel.querySelector('.gvs-mobile-menu__guide[href]');
      if (!(guide instanceof HTMLAnchorElement)) return;

      const guideUrl = normaliseMenuUrl(guide.href);
      if (!guideUrl) return;

      panel
        .querySelectorAll('.gvs-mobile-menu__link-list--final a[href]')
        .forEach((link) => {
          if (!(link instanceof HTMLAnchorElement)) return;
          if (normaliseMenuUrl(link.href) !== guideUrl) return;

          const item = link.closest('.gvs-mobile-menu__link-item');
          if (!(item instanceof HTMLElement)) return;

          item.hidden = true;
          item.setAttribute('aria-hidden', 'true');
          item.dataset.gvsDuplicateGuideRow = 'true';
        });
    });
  }

  /** @returns {boolean} Whether the root drawer is open. */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Close the deepest active panel when Escape is pressed. At the root, close the drawer.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Clear the drawer synchronously before a normal link leaves the page.
   * This prevents browsers and Shopify page transitions restoring it as open.
   * @param {MouseEvent} event
   */
  #onNavigationClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;

    this.#resetDrawer(false);
  };

  /** Reset the drawer before the page is stored in the back-forward cache. */
  #onPageHide = () => {
    this.#resetDrawer(false);
  };

  /** Reset restored pages, including pages returned from the back-forward cache. */
  #onPageShow = () => {
    this.#resetDrawer(false);
  };

  /**
   * Reset the mobile drawer when the desktop header takes over.
   * @param {MediaQueryListEvent} event
   */
  #onViewportChange = (event) => {
    if (!event.matches) return;

    this.#resetDrawer(false);
  };

  /**
   * Find the nearest details element for an event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /** Toggle the root drawer. */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the nearest drawer panel.
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (details !== this.refs.details) {
        const backButton = details.querySelector(
          ':scope > .gvs-mobile-menu__panel .gvs-mobile-menu__back'
        );

        backButton?.focus({ preventScroll: true });
      }
    });

    const focusScope =
      details === this.refs.details
        ? this.querySelector('[data-header-drawer]')
        : details.querySelector(':scope > .gvs-mobile-menu__panel');

    trapFocus(focusScope ?? details);
  }

  /**
   * Return to the previous panel.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /** Close the complete mobile drawer. */
  close() {
    this.#close(this.refs.details);
  }

  /** Close the drawer, then open the theme's existing predictive-search modal. */
  openSearch() {
    const searchModal = document.querySelector('#search-modal');

    this.close();

    window.setTimeout(() => {
      if (searchModal && typeof searchModal.showDialog === 'function') {
        searchModal.showDialog();
      }
    }, 220);
  }

  /**
   * Close one panel or the root drawer after its transition completes.
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      const returningSummary = summary;

      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();

        this.querySelectorAll('details[open]').forEach(reset);
        this.#releaseScrollLock();
        returningSummary.focus({ preventScroll: true });

        return;
      }

      const parentDetails =
        details.parentElement?.closest('details') ?? this.refs.details;

      const focusScope =
        parentDetails === this.refs.details
          ? this.querySelector('[data-header-drawer]')
          : parentDetails.querySelector(
              ':scope > .gvs-mobile-menu__panel'
            );

      trapFocus(focusScope ?? parentDetails);
      returningSummary.focus({ preventScroll: true });
    });
  }

  /**
   * Immediately return every drawer panel to its initial state.
   * @param {boolean} restoreFocus
   */
  #resetDrawer(restoreFocus = true) {
    const rootSummary =
      this.refs.details.querySelector(':scope > summary');

    this.querySelectorAll('details').forEach(reset);

    removeTrapFocus();
    this.#releaseScrollLock();

    if (restoreFocus) {
      rootSummary?.focus({ preventScroll: true });
    }
  }

  /** Remove scroll locking only when no other open component still requires it. */
  #releaseScrollLock() {
    const otherOpenScrollLock = Array.from(
      document.querySelectorAll('[scroll-lock][open]')
    ).some((element) => !this.contains(element));

    if (!otherOpenScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}


/**
 * Normalise a navigation URL for exact-route comparison.
 * @param {string} value
 * @returns {string}
 */
function normaliseMenuUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    const pathname = url.pathname.replace(/\/+$/, '') || '/';
    return `${url.origin}${pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector(':scope > summary')
    ?.setAttribute('aria-expanded', 'false');
}